export type Quality = 'HIGH' | 'NORMAL' | 'LOW'

export type ContentType =
  | 'MOVIE' | 'SERIES' | 'DOCUMENTARY' | 'NEWS' | 'MAGAZINE' | 'TV_SHOW' | 'SPORT'
  | 'CHILDREN' | 'MUSIC' | 'KNOWLEDGE' | 'PODCAST' | 'LIVE_STREAM' | 'OTHER' | 'UNKNOWN'

export type ContentTag = 'DOCUMENTARY' | 'NEWS' | 'MAGAZINE' | 'SPORT' | 'MUSIC' | 'CHILDREN' | 'KNOWLEDGE'

export interface ContentClassification {
  type: ContentType
  inferredType: ContentType
  tags: ContentTag[]
  inferredTags: ContentTag[]
  confidence: number
  source: 'ALGORITHM' | 'MANUAL'
  evidence: string[]
  algorithmVersion: string
}

export interface Film {
  id: string
  sender: string
  topic: string
  title: string
  description: string
  broadcastAt: string | null
  durationSeconds: number
  sizeMb: number | null
  normalUrl: string
  lowUrl: string
  highUrl: string
  subtitleUrl: string
  websiteUrl: string
  geo: string
  newFilm: boolean
  livestream: boolean
  playlist: boolean
  subtitles: boolean
  highQuality: boolean
  audioDescription: boolean
  signLanguage: boolean
  originalLanguage: boolean
  bookmarked: boolean
  seen: boolean
  classification: ContentClassification
  library: LibraryAvailability
  audioVariants: AudioVariant[]
}

export interface AudioVariant {
  filmId: string
  label: string
  languageCode: string
  title: string
  durationSeconds: number
  sizeMb: number | null
}

export interface DiscoveryItem {
  eventId: number
  film: Film
  eventType: 'BASELINE' | 'ADDED' | 'REMOVED' | 'REAPPEARED'
  occurredAt: string
  available: boolean
  reappearanceCount: number
  metadata: MediaMetadata | null
  metadataEnrichment: MetadataEnrichmentState
  library: LibraryAvailability
}

export type SeriesNumberingScheme = 'NUMBERED' | 'YEAR' | 'PART_OF_TOTAL' | 'NONE'

export interface SeriesSummary {
  id: string
  title: string
  sender: string
  description: string
  discoveryKind: 'BASELINE' | 'NEW_SERIES' | 'NEWLY_CLASSIFIED'
  firstSeenAt: string
  latestEpisodeAt: string | null
  availableEpisodeCount: number
  unnumberedEpisodeCount: number
  queuedEpisodeCount: number
  downloadedEpisodeCount: number
  followed: boolean
  subscription: {
    id: string
    active: boolean
    executionMode: 'AUTO' | 'QUEUE_ONLY'
  } | null
  latestEpisode: {
    filmId: string
    title: string
    broadcastAt: string | null
    numberingScheme: SeriesNumberingScheme
    seasonNumber: number | null
    episodeNumber: number | null
    seasonLabel: string | null
    partNumber: number | null
    partTotal: number | null
  } | null
  groupingSource: string
  groupingConfidence: number
}

export interface SeriesEpisode {
  filmId: string
  title: string
  description: string
  sender: string
  topic: string
  broadcastAt: string | null
  durationSeconds: number
  available: boolean
  subtitles: boolean
  highQuality: boolean
  numberingScheme: SeriesNumberingScheme
  seasonNumber: number | null
  episodeNumber: number | null
  seasonLabel: string | null
  partNumber: number | null
  partTotal: number | null
  numberingConfidence: number
  numberingSource: string
}

export interface SeriesIndexStatus {
  complete: boolean
  baselineInitialized: boolean
  algorithmVersion: string | null
  indexedGeneration: string | null
  targetGeneration: string | null
  processedCount: number
  completedAt: string | null
}

export interface MetadataEnrichmentState {
  status: 'PENDING' | 'MATCHED' | 'NOT_FOUND' | 'AMBIGUOUS' | 'ERROR'
  attemptedAt: string | null
  retryAfter: string | null
  detail: string | null
}

export interface LibraryAvailability {
  downloaded: boolean
  copies: LibraryCopy[]
}

export interface LibraryCopy {
  downloadProfileId: string | null
  downloadProfileName: string | null
  downloadProfileActive: boolean
  mediaRootId: string
  mediaRootLabel: string
  relativePath: string
  sizeBytes: number
  downloadedAt: string
  detection: 'TRACKED_DOWNLOAD' | 'PROFILE_PATH'
}

export interface MediaMetadata {
  provider: string
  providerId: string
  imdbId: string | null
  mediaType: string
  title: string
  originalTitle: string | null
  releaseYear: number | null
    fskRating: number | null
    overview: string | null
    genres: string[]
    directors: string[]
    countries: string[]
    languages: string[]
    matchConfidence: number
    providerUrl: string | null
    attribution: string | null
    artworkSourceUrl: string | null
    artworkCreator: string | null
    artworkLicense: string | null
    artworkLicenseUrl: string | null
    artworkAvailable: boolean
    artworkManual: boolean
    artworkUpdatedAt: string | null
}

export interface ArtworkSelection {
  imageUrl: string
  sourceUrl?: string | null
  creator?: string | null
  license?: string | null
  licenseUrl?: string | null
}

export interface ArtworkSearchCandidate extends ArtworkSelection {
  id: string
  title: string
  thumbnailUrl: string
  creatorUrl: string | null
  sourceName: string
  width: number | null
  height: number | null
}

export interface ArtworkSearchResponse {
  query: string
  provider: string
  results: ArtworkSearchCandidate[]
}

export interface BroadcasterArtworkSuggestionResponse {
  result: ArtworkSearchCandidate | null
}

export interface CatalogRule {
  id: string
  name: string
  mode: 'BLACKLIST' | 'WHITELIST'
  active: boolean
  sender: string
  topic: string
  titleContains: string
  anywhereContains: string
  createdAt: string
  updatedAt: string
}

export type SaveCatalogRule = Omit<CatalogRule, 'id' | 'createdAt' | 'updatedAt'>

export interface SavedFilter {
  id: string
  name: string
  definition: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface Page<T> {
  items: T[]
  page: number
  size: number
  totalItems: number
  totalPages: number
}

export interface Facet {
  value: string
  count: number
}

export type DownloadState =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'UNSUPPORTED_SOURCE'
  | 'SKIPPED'
  | 'DELETED'

export interface DownloadJob {
  id: string
  filmId: string
  subscriptionId: string | null
  downloadProfileId: string | null
  downloadProfileName: string | null
  sourceType: 'MANUAL' | 'SUBSCRIPTION'
  state: DownloadState
  processingStage: 'VIDEO' | 'WAITING_FOR_AUDIO' | 'SIDECARS' | 'FINALIZING' | null
  startAutomatically: boolean
  quality: Quality
  selectedUrl: string
  originalUrl: string
  fallbackUsed: boolean
  sender: string
  topic: string
  title: string
  websiteUrl: string
  subtitleUrl: string
  mediaRootId: string
  destinationSubpath: string
  targetFilename: string
  subtitlesRequested: boolean
  subtitlesEnabled: boolean
  subtitleState: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | null
  infoFileEnabled: boolean
  infoFileState: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | null
  bytesDownloaded: number
  totalBytes: number | null
  progressPercent: number
  retryCount: number
  errorCode: string | null
  errorDetail: string | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  updatedAt: string
  audioTracks: Array<{
    filmId: string
    label: string
    languageCode: string
    filename: string
    state: 'QUEUED' | 'PREPARING' | 'DOWNLOADING' | 'EXTRACTING' | 'READY' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
    bytesDownloaded: number
    totalBytes: number | null
    processingProgressPercent: number
    errorDetail: string | null
  }>
}

export interface DownloadTargetPreview {
  mediaRootId: string
  relativeDirectory: string
  containerDirectory: string
  files: Array<{
    type: 'VIDEO' | 'AUDIO' | 'SUBTITLE' | 'INFO'
    filenames: string[]
    containerPaths: string[]
    alternatives: boolean
  }>
}

export interface DownloadArtifact {
  id: string
  downloadJobId: string
  type: 'VIDEO' | 'AUDIO_EXTERNAL' | 'SUBTITLE_SOURCE' | 'SUBTITLE_SRT' | 'SUBTITLE_ASS' | 'INFO'
  mediaRootId: string
  relativePath: string
  sizeBytes: number
  createdAt: string
  deletedAt: string | null
}

export interface Subscription {
  id: string
  name: string
  active: boolean
  sender: string
  topic: string
  titleFilter: string
  topicTitleFilter: string
  anywhereFilter: string
  filterMode: 'TEXT' | 'REGEX' | 'DESKTOP_REGEX'
  minimumDurationSeconds: number | null
  maximumDurationSeconds: number | null
  onlyNew: boolean
  excludeAudioDescription: boolean
  excludeSignLanguage: boolean
  excludeOriginalLanguage: boolean
  onlyOriginalLanguage: boolean
  includeAdditionalAudio: boolean
  executionMode: 'AUTO' | 'QUEUE_ONLY'
  downloadProfileId: string | null
  quality: Quality
  mediaRootId: string
  destinationSubpath: string
  subtitlesEnabled: boolean
  infoFileEnabled: boolean
  createdAt: string
  updatedAt: string
  lastScannedAt: string | null
  desktopLastDownload: string
  programmeId: string | null
}

export type SaveSubscription = Omit<Subscription, 'id' | 'createdAt' | 'updatedAt' | 'lastScannedAt' | 'desktopLastDownload'>

export interface SubscriptionStatistics {
  queued: number
  running: number
  downloaded: number
  failed: number
}

export interface BulkSubscriptionUpdate {
  ids: string[]
  active?: boolean
  executionMode?: Subscription['executionMode']
}

export interface SubscriptionPreview {
  items: Film[]
  targets: Record<string, { mediaRootId: string; relativePath: string }>
  outcomes: Record<string, {
    status: 'READY' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'UNSUPPORTED_SOURCE' | 'SKIPPED' | 'HISTORY' | 'LIBRARY'
    downloadJobId: string | null
    updatedAt: string | null
  }>
  offset: number
  limit: number
  hasMore: boolean
}

export interface SubscriptionHistoryEntry {
  subscriptionId: string
  subscriptionName: string
  downloadJobId: string | null
  filmId: string | null
  sender: string
  topic: string
  title: string
  sourceUrl: string
  completedAt: string
}

export interface DownloadProfile {
  id: string
  name: string
  active: boolean
  mediaRootId: string
  baseSubpath: string
  createTopicFolder: boolean
  filenameTemplate: string
  quality: Quality
  subtitlesEnabled: boolean
  infoFileEnabled: boolean
  createdAt: string
  updatedAt: string
}

export type SaveDownloadProfile = Omit<DownloadProfile, 'id' | 'createdAt' | 'updatedAt'>

export interface MediaRoot {
  id: string
  label: string
  path: string
  writable: boolean
}

export interface StorageRoot extends MediaRoot {
  totalBytes: number | null
  usableBytes: number | null
}

export interface MediaDirectory {
  name: string
  path: string
  writable: boolean
}

export interface MediaDirectoryListing {
  path: string
  parentPath: string | null
  writable: boolean
  truncated: boolean
  directories: MediaDirectory[]
}

export interface CatalogState {
  status: 'EMPTY' | 'DOWNLOADING' | 'IMPORTING' | 'READY' | 'FAILED'
  sourceId: string | null
  sourceGeneratedAt: string | null
  refreshStartedAt: string | null
  refreshFinishedAt: string | null
  importedCount: number
  errorCode: string | null
  errorDetail: string | null
}

export interface SystemStatus {
  version: string
  buildTime: string | null
  commitId: string
  serverTime: string
  databaseReady: boolean
  catalog: CatalogState
  catalogItems: number
  mediaBasePath: string
  mediaRoots: StorageRoot[]
  metadata: {
    provider: string
    enabled: boolean
  }
}

export interface MetadataEnrichmentStatus {
  provider: string
  enabled: boolean
  lookupVersion: number
  batchSize: number
  batchDelaySeconds: number
  progress: {
    eligible: number
    attempted: number
    pending: number
    baselinePending: number
    retryScheduled: number
    matched: number
    withArtwork: number
    withoutArtwork: number
    notFound: number
    ambiguous: number
    failed: number
    lastAttemptAt: string | null
  }
  worker: {
    running: boolean
    batchStartedAt: string | null
    batchFinishedAt: string | null
    batchTotal: number
    batchProcessed: number
    batchMatched: number
    batchWithArtwork: number
  }
}

export interface AuthUser {
  username: string
  expiresAt: string | null
}

export interface AppNotification {
  id: string
  severity: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'
  code: string
  titleKey: string
  messageKey: string
  arguments: Record<string, unknown>
  referenceType: string | null
  referenceId: string | null
  read: boolean
  createdAt: string
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message)
  }
}

let csrfToken: string | null = null
let csrfRequest: Promise<string> | null = null
let refreshRequest: Promise<AuthUser | null> | null = null

export function resetCsrfToken() {
  csrfToken = null
  csrfRequest = null
}

export async function ensureCsrfToken() {
  if (csrfToken) return csrfToken
  if (!csrfRequest) {
    csrfRequest = fetch('/api/v1/auth/csrf', { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new ApiError(`HTTP ${response.status}`, response.status)
        const body = await response.json() as { token: string }
        csrfToken = body.token
        return body.token
      })
      .finally(() => { csrfRequest = null })
  }
  return csrfRequest
}

export async function refreshAuthentication(): Promise<AuthUser | null> {
  if (!refreshRequest) {
    refreshRequest = ensureCsrfToken()
      .then((token) => fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'X-MediathekView-Request': '1',
          'X-CSRF-TOKEN': token,
        },
      }))
      .then(async (response) => {
        if (response.status === 401) return null
        if (!response.ok) {
          const problem = await response.json().catch(() => null)
          throw new ApiError(problem?.detail ?? `HTTP ${response.status}`, response.status, problem?.code)
        }
        const authenticatedUser = await response.json() as AuthUser
        window.dispatchEvent(new CustomEvent<AuthUser>('mediathekview-auth-refreshed', {
          detail: authenticatedUser,
        }))
        return authenticatedUser
      })
      .finally(() => { refreshRequest = null })
  }
  return refreshRequest
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (init?.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const unsafe = init?.method && !['GET', 'HEAD', 'OPTIONS'].includes(init.method.toUpperCase())
  if (unsafe) {
    headers.set('X-MediathekView-Request', '1')
    headers.set('X-CSRF-TOKEN', await ensureCsrfToken())
  }
  let response = await fetch(path, { ...init, headers, credentials: 'same-origin', cache: 'no-store' })
  const canRefresh = !['/api/v1/auth/login', '/api/v1/auth/refresh', '/api/v1/auth/csrf'].includes(path)
  if (response.status === 401 && canRefresh) {
    const authenticatedUser = await refreshAuthentication()
    if (authenticatedUser) {
      response = await fetch(path, { ...init, headers, credentials: 'same-origin', cache: 'no-store' })
    }
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    if (response.status === 401 && canRefresh) {
      window.dispatchEvent(new Event('mediathekview-auth-required'))
    }
    throw new ApiError(problem?.detail ?? `HTTP ${response.status}`, response.status, problem?.code)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function queryString(values: Record<string, string | number | boolean | null | undefined>) {
  const params = new URLSearchParams()
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  })
  return params.toString()
}

export function formatDuration(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return hours ? `${hours}:${String(minutes).padStart(2, '0')} h` : `${minutes} min`
}

export function formatBytes(bytes: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / 1024 ** exponent).toFixed(exponent > 1 ? 1 : 0)} ${units[exponent]}`
}
